import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/templates/PageLayout';
import Button from '../components/atoms/Button';
import InquiryEmbed from '../components/molecules/InquiryEmbed';
import { activeOffer } from '../data/activeOffer';
import { facts } from '../data/facts';
import { INQUIRY_HOSTED_URL } from '../data/inquiryForm';
import { prefersReducedMotion } from '../utils/hashNavigation';

const linkClass =
  'text-blue-600 underline hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow';

const Contact = () => {
  useEffect(() => {
    document.title = 'Contact | DataRook';
  }, []);

  const scrollToForm = () => {
    document.getElementById('contact-form')?.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  };

  return (
    <PageLayout>
      <div className="dr-container py-16">
        <h1 className="text-4xl font-bold mb-8 text-center">Contact Us</h1>

        <div className="max-w-4xl mx-auto">
          <p className="text-xl mb-4 text-center">
            Have questions about footyLab or want to bring our innovative STEM program to your
            community? We&apos;re excited to hear from you.
          </p>
          <p className="text-lg mb-4 text-center text-gray-700 max-w-2xl mx-auto">
            <strong>Parents and families:</strong> tell us about your student and upcoming program
            interest.
          </p>
          <p className="text-lg mb-8 text-center text-gray-700 max-w-2xl mx-auto">
            <strong>Schools and community partners:</strong> share your organization, timeline, and
            how you&apos;d like footyLab to fit your students.
          </p>
          <p className="text-lg mb-8 text-center text-gray-700 max-w-2xl mx-auto">
            Express interest in upcoming camps and programs using the form below. {activeOffer.nextStep}
          </p>

          <div className="mb-12">
            <h2 className="text-2xl font-semibold mb-4">Send Us a Message</h2>
            <p className="mb-4 text-gray-700">
              This is an interest inquiry, not program registration. Please use adult contact
              details and do not include children&apos;s names, birth dates, medical information,
              passwords, or GPS files. We review website inquiries manually; this does not
              automatically create a CRM contact.
            </p>
            <InquiryEmbed />
            <p className="mt-4 text-sm text-gray-600 max-w-2xl">
              Notion receives whatever you type in this form so we can reply.{' '}
              {activeOffer.nextStep} You can also{' '}
              <a href={INQUIRY_HOSTED_URL} target="_blank" rel="noopener noreferrer" className={linkClass}>
                open the form on Notion
              </a>{' '}
              or email{' '}
              <a href="mailto:info@datarook.com" className={linkClass}>
                info@datarook.com
              </a>
              .{' '}
              <Link
                to="/privacy"
                className="text-navy underline hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow"
              >
                Privacy Policy
              </Link>
            </p>
          </div>

          <div className="mb-12">
            <h2 className="text-2xl font-semibold mb-4">Get in Touch</h2>
            <p className="mb-4">
              The best way to reach us is through the form above. If you prefer, you can also email
              us directly at:{' '}
              <a href="mailto:info@datarook.com" className={linkClass}>
                info@datarook.com
              </a>
            </p>
            <div className="mt-6">
              <h3 className="text-xl font-semibold mb-2">Connect With Us</h3>
              <div className="flex space-x-4">
                <a
                  href="https://www.linkedin.com/company/datarook"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={linkClass}
                >
                  <i className="fab fa-linkedin" /> LinkedIn
                </a>
                <a
                  href="https://www.youtube.com/@datarook"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-red-600 underline hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow"
                >
                  <i className="fab fa-youtube" /> YouTube
                </a>
                <a
                  href="https://www.instagram.com/datarook_official"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-pink-600 underline hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow"
                >
                  <i className="fab fa-instagram" /> Instagram
                </a>
              </div>
            </div>
          </div>

          <div className="mb-12">
            <h2 className="text-2xl font-semibold mb-4">Frequently Asked Questions</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold">
                  How can I bring footyLab to my school or community?
                </h3>
                <p>
                  Contact us using the form above or send an email to info@datarook.com. We&apos;ll be
                  happy to discuss how we can tailor our program to your needs.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold">What age group is footyLab designed for?</h3>
                <p>{facts.ageSentence}</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold">Do participants need prior coding experience?</h3>
                <p>
                  No prior coding experience is necessary. Our curriculum is designed to accommodate
                  beginners while also challenging more experienced students.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <h2 className="text-2xl font-semibold mb-4">Ready to Get Started?</h2>
            <Button onClick={scrollToForm}>Contact Us Now</Button>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Contact;
